import { isBillCheckingTargeted, isSaveTargeted } from "../../lib/auth/utils";
import type { FlowState } from "../../lib/flow-engine";
import type { OnboardingConfig } from "./OnboardingFlowV3";
import type { Intent, OnboardingIdentity, OnboardingPartner } from "./types";

export type OnboardingStateData = {
  hasFaAnswers: boolean;
  hasIdentity: boolean;
  /**
   * The user answered the insurance step — by selecting a carrier
   * (segmentation `hasInsurance`) or by declaring they are uninsured
   * (a recorded `user.profile.insuranceStatus`). "Uninsured" is an answer:
   * resume must not reopen the step over it.
   */
  hasInsuranceAnswer: boolean;
  identity: OnboardingIdentity | null;
  /**
   * Segmentation features; intent is read from here. Sourced from the same
   * fresh network-only query as the other flags — not the auth-context user
   * snapshot, whose freshness depends on the wrapper's load ordering.
   */
  features: readonly string[] | null;
  /** From `viewerPartner` — null when the user did not join via a partner. */
  partner: OnboardingPartner | null;
  /** Server-stamped acceptance; `null` means never accepted. */
  acceptedTermsAt: string | null;
};

// FlowState literals (the engine's documented `initial` shape) instead of
// `Screen.X()` constructors keep this module free of the flow's React imports.
export function deriveInitial(
  state: OnboardingStateData,
): FlowState<OnboardingConfig> {
  const features = state.features;
  // `undefined`, not `null` — must match the shape Profile threads down.
  const identity = state.identity ?? undefined;
  const partner = state.partner;
  const intent: Intent | undefined = isBillCheckingTargeted(features)
    ? "bill"
    : isSaveTargeted(features)
      ? "save"
      : undefined;

  // Identity-first: with nothing usable persisted, entry is the profile step.
  const topOfFunnel: FlowState<OnboardingConfig> = {
    tag: "Profile",
    data: { partner },
  };

  if (!state.hasIdentity) {
    return topOfFunnel;
  }

  // Terms are the one step the rest of the flow cannot stand in for: an
  // account preseeded outside onboarding can hold identity, intent and
  // insurance with terms never accepted, which would otherwise derive
  // `Complete`, bounce home, and re-enter the resume gate on every mount.
  if (state.acceptedTermsAt === null) {
    return topOfFunnel;
  }

  if (intent === undefined) {
    return { tag: "Intent", data: { identity, partner } };
  }
  // A no-partner save user never resumes into the screener, so their
  // `hasFaAnswers` is not consulted.
  if (intent === "save" && partner !== null && !state.hasFaAnswers) {
    return {
      tag: "FinancialAssistance",
      data: { intent, identity, partner },
    };
  }
  if (!state.hasInsuranceAnswer) {
    return { tag: "Insurance", data: { intent, identity } };
  }
  return { tag: "Complete", data: {} };
}
